import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Star as StarIcon
} from '@mui/icons-material';

export default function GroupAnalytics({ groups, currentUser }) {
  if (!groups || groups.length === 0) {
    return null;
  }

  // Calculate analytics
  const analytics = {
    totalGroups: groups.length,
    totalMembers: groups.reduce((total, group) => total + (group.memberCount || 1), 0),
    adminGroups: groups.filter(group => group.createdBy === currentUser?.uid).length,
    memberGroups: groups.filter(group => group.createdBy !== currentUser?.uid).length,
    averageMembers: Math.round(groups.reduce((total, group) => total + (group.memberCount || 1), 0) / groups.length),
    largestGroup: groups.reduce((largest, group) => (group.memberCount || 1) > (largest?.memberCount || 1) ? group : largest, null),
    mostRecentGroup: groups.reduce((recent, group) => {
      const recentDate = recent?.updatedAt?.toDate?.() || recent?.createdAt?.toDate?.() || new Date(0);
      const groupDate = group?.updatedAt?.toDate?.() || group?.createdAt?.toDate?.() || new Date(0);
      return groupDate > recentDate ? group : recent;
    }, null),
    privacyBreakdown: groups.reduce((acc, group) => {
      acc[group.privacy] = (acc[group.privacy] || 0) + 1;
      return acc;
    }, {})
  };

  // Get top groups by member count
  const topGroups = [...groups]
    .sort((a, b) => (b.memberCount || 1) - (a.memberCount || 1))
    .slice(0, 3);

  // Get recent activity (groups updated in last 7 days)
  const recentActivity = groups.filter(group => {
    const updatedDate = group.updatedAt?.toDate?.() || group.createdAt?.toDate?.() || new Date(0);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return updatedDate > sevenDaysAgo;
  });

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon color="primary" />
        Group Analytics & Insights
      </Typography>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Key Metrics
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Group Growth</Typography>
              <Typography variant="body2" color="primary">
                {analytics.totalGroups} groups
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((analytics.totalGroups / 10) * 100, 100)} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Member Reach</Typography>
              <Typography variant="body2" color="primary">
                {analytics.totalMembers} members
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min((analytics.totalMembers / 50) * 100, 100)} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Leadership</Typography>
              <Typography variant="body2" color="primary">
                {analytics.adminGroups} admin groups
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(analytics.adminGroups / analytics.totalGroups) * 100} 
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        </Grid>

        {/* Privacy Distribution */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            Privacy Distribution
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Object.entries(analytics.privacyBreakdown).map(([privacy, count]) => (
              <Box key={privacy} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={privacy} 
                    size="small" 
                    variant="outlined"
                    color={privacy === 'private' ? 'default' : privacy === 'friends' ? 'primary' : 'warning'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {count} groups
                </Typography>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom color="primary">
            Quick Stats
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Average Group Size:</Typography>
              <Typography variant="body2" color="primary" fontWeight="bold">
                {analytics.averageMembers} members
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Recent Activity:</Typography>
              <Typography variant="body2" color="primary" fontWeight="bold">
                {recentActivity.length} groups
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Top Groups */}
      <Typography variant="subtitle1" gutterBottom color="primary">
        Top Groups by Size
      </Typography>
      
      <Grid container spacing={2}>
        {topGroups.map((group, index) => (
          <Grid item xs={12} sm={6} md={4} key={group.id}>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: index === 0 ? 'warning.main' : 'primary.main' }}>
                  {index === 0 ? <StarIcon /> : <GroupIcon />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {group.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {group.memberCount || 1} members
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {group.privacy} • {group.createdBy === currentUser?.uid ? 'Admin' : 'Member'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="subtitle1" gutterBottom color="primary">
            Recent Activity
          </Typography>
          
          <List dense>
            {recentActivity.slice(0, 5).map((group) => (
              <ListItem key={group.id} sx={{ px: 0 }}>
                <ListItemIcon>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    <GroupIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={group.name}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Updated {group.updatedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.memberCount || 1} members • {group.privacy}
                      </Typography>
                    </Box>
                  }
                />
                <Chip 
                  label={group.createdBy === currentUser?.uid ? 'Admin' : 'Member'} 
                  size="small" 
                  color={group.createdBy === currentUser?.uid ? 'primary' : 'default'}
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Paper>
  );
}
